import { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FJModal from '../FJModal';
import FJPreviewModal from './FJPreviewModal';
import FJConstant from '../../../constants/FJConstant';
import useLocalStore from '../../../hooks/useLocalStore';
import FJActionModal from '../../../redux/action/FJActionModal';
import FJActionCommon from '../../../redux/action/FJActionCommon';
import FJRequirementsUtil from '../../../util/FJRequirementsUtil.js';
import FJActionEditPage from '../../../redux/action/FJActionEditPage.js';
import FJAppConstant from '../../../constants/FJAppConstant.js';

const FJPreviewModalContainer = () => {
    useLocalStore();
    const dispatch = useDispatch();
    const previewModalData = useSelector(state => state.modals.previewModalData);
    const historyList = useSelector(state => state.common.historyList);
    const creationList = useSelector(state => state.common.creationList);
    const appType = useSelector(state => state.common.appType);
    const isApplyButton = useSelector(state => state.common.isApplyButton);
    const addAsEditPageModalBtuStatus = useSelector(state => state.modals.addAsEditPageModalBtuStatus);
    const { display, data = {} } = previewModalData;
    const { id, type, imgUrl, videoUrl, audioUrl, width, height, garment } = data;
    const sortType = useSelector(state => state.AIVideoGenerator.sortType);
    const isAudioGenerator = appType === FJAppConstant.AI_AUDIO_GENERATOR;
    const isVideoGeneration = appType === FJAppConstant.AI_VIDEO_GENERATOR;
    const isVideoRemoveBg = appType === FJAppConstant.VIDEO_REMOVE_BG;
    const isVideoDub = appType === FJAppConstant.AI_VIDEO_DUB;
    const historyTabIndex = useSelector(state => state.AIAudioGenerator.historyTabIndex);

    const onClose = useCallback(() => {
        dispatch(FJActionModal.hidePreviewModalDataAction());
    }, [dispatch]);

    const onDownload = callback => {
        dispatch(FJActionCommon.multiDownloadAction([id], 'preview', callback));
    };
    const onDelete = () => {
        let index = previewList.findIndex(item => item.id === id);
        dispatch(
            FJActionCommon.multiDeleteAction([id], list => {
                if (!list) return;
                if (!list.length) {
                    onClose();
                    return;
                }

                if (isAudioGenerator || isVideoGeneration) {
                    const tempList = isVideoGeneration
                        ? FJRequirementsUtil.getSortHistoryList(list, sortType)
                        : FJRequirementsUtil.getAudioHistoryList(list, historyTabIndex);

                    let data = null;
                    for (let i = index; i < tempList.length; i++) {
                        const item = tempList[i];
                        if (item.status === 'completed') {
                            data = item;
                            break;
                        }
                    }
                    if (!data) {
                        for (let i = index; i >= 0; i--) {
                            const item = tempList[i];
                            if (item && item.status === 'completed') {
                                data = item;
                                break;
                            }
                        }
                    }
                    if (data) {
                        previewImage(data);
                    } else {
                        onClose();
                    }
                } else {
                    index >= list.length && (index = list.length - 1);
                    previewImage(list[index]);
                }
            }),
        );
    };
    const addAsScene = () => {
        dispatch(FJActionModal.addAsEditPageAction([id]));
    };
    const _addToMedia = () => {
        let url = imgUrl;
        if (isVideoGeneration) url = videoUrl;
        else if (isAudioGenerator) url = audioUrl;
        dispatch(FJActionEditPage.addToMediaLibraryAction(url, { from: 'generate' }));
    };
    const _onApply = () => {
        let url = imgUrl;
        if (isVideoGeneration) url = videoUrl;
        else if (isAudioGenerator) url = audioUrl;
        onClose();
        let options;
        if (width && height) {
            options = {
                size: { width: width, height: height },
            };
        }
        dispatch(FJActionEditPage.applyToEditPageAction(url, options));
    };
    const shareToSociety = type => {
        dispatch(FJActionModal.shareToSocietyAction(id, type));
    };
    const previewImage = data => {
        dispatch(FJActionModal.showPreviewModalDataAction({ data }));
    };
    const previewList = useMemo(() => {
        if (isAudioGenerator) {
            return FJRequirementsUtil.getAudioHistoryList(historyList, historyTabIndex);
        }
        if (type === FJConstant.HISTORY) {
            if (isVideoGeneration) {
                return FJRequirementsUtil.getSortHistoryList(historyList, sortType);
            } else {
                return historyList;
            }
        } else {
            return creationList;
        }
    }, [type, historyList, creationList, isVideoGeneration, sortType, isAudioGenerator, historyTabIndex]);
    const switchPreview = (isNext = true) => {
        const index = previewList.findIndex(item => item.id === id);
        const nextIndex = isNext ? index + 1 : index - 1;
        if (previewList[nextIndex]) {
            const item = previewList[nextIndex];
            if (isVideoGeneration || isAudioGenerator) {
                if (item.status === 'completed') {
                    previewImage(item);
                } else {
                    changePreviewByIndex(nextIndex, isNext);
                }
            } else {
                previewImage(item);
            }
        }
    };

    const changePreviewByIndex = (index, isNext) => {
        const nextIndex = isNext ? index + 1 : index - 1;
        if (previewList[nextIndex]) {
            const item = previewList[nextIndex];
            if (item.status === 'completed') {
                previewImage(item);
            } else {
                changePreviewByIndex(nextIndex, isNext);
            }
        }
    };

    const isActiveNext = useMemo(() => {
        if (isVideoGeneration || isAudioGenerator) {
            const currentIndex = previewList.findIndex(item => item.id === id);
            const willPreviewList = previewList.slice(currentIndex + 1);
            return currentIndex < previewList.length - 1 && willPreviewList.some(item => item.status === 'completed');
        } else {
            return previewList.findIndex(item => item.id === id) < previewList.length - 1;
        }
    }, [id, previewList, isAudioGenerator, isVideoGeneration]);
    const isActivePrevious = useMemo(() => {
        if (isVideoGeneration || isAudioGenerator) {
            const currentIndex = previewList.findIndex(item => item.id === id);
            const willPreviewList = previewList.slice(0, currentIndex);
            return currentIndex > 0 && willPreviewList.some(item => item.status === 'completed');
        } else {
            return previewList.findIndex(item => item.id === id) > 0;
        }
    }, [id, previewList, isAudioGenerator, isVideoGeneration]);

    const onEdit = useCallback(() => {
        onClose();
        dispatch(FJActionCommon.editHistoryItemAction(data, null, { position: 'preview' }));
    }, [dispatch, data, onClose]);

    return (
        <FJModal visible={display}>
            <FJPreviewModal
                isApplyButton={isApplyButton}
                appType={appType}
                addAsScene={addAsScene}
                addToMedia={_addToMedia}
                onApply={_onApply}
                shareToSociety={shareToSociety}
                onClose={onClose}
                data={data}
                onDownload={onDownload}
                onDelete={onDelete}
                onEdit={onEdit}
                isActiveNext={isActiveNext}
                isActivePrevious={isActivePrevious}
                switchPreview={switchPreview}
                addAsEditPageModalBtuStatus={addAsEditPageModalBtuStatus}
                active={display}
                isAudioGenerator={isAudioGenerator}
                isVideoGeneration={isVideoGeneration}
                isVideoRemoveBg={isVideoRemoveBg}
                isVideoDub={isVideoDub}
                garment={garment}
            />
        </FJModal>
    );
};
export default FJPreviewModalContainer;
